// Which colour a thing is, and what it is called.
//
// Two rules, and they are the reason this is a module rather than a literal in
// each chart:
//
//  1. A CATEGORY keeps its colour everywhere. Telephony is the same blue on the
//     day chart, the breakdown and the trend, so one legend is learned once.
//  2. Anything else takes the next slot IN ORDER, never a cycled or generated
//     hue. Colour follows the entity and not its rank, so the assignment is made
//     from the position of a key in the whole ordered list — a filter that drops
//     one series therefore does not repaint the ones that stay.
//
// The slots themselves live in tokens.css, where the palette is validated as a
// set. Nothing here holds a hex.

/** The categories `crmUsageCategoryOf` derives, in legend order. */
export const USAGE_CATEGORIES = ["telephony", "gen_ai", "tts", "stt", "other"] as const;

export type UsageCategory = (typeof USAGE_CATEGORIES)[number];

const CATEGORY_COLORS: Record<UsageCategory, string> = {
  telephony: "var(--cat-telephony)",
  gen_ai: "var(--cat-gen-ai)",
  tts: "var(--cat-tts)",
  stt: "var(--cat-stt)",
  other: "var(--cat-other)",
};

const CATEGORY_LABELS: Record<UsageCategory, string> = {
  telephony: "telephony",
  gen_ai: "gen AI",
  tts: "TTS",
  stt: "STT",
  other: "other",
};

/** The fixed order. A seventh series folds into "other" rather than inventing a hue. */
const SERIES_SLOTS = [
  "var(--series-1)",
  "var(--series-2)",
  "var(--series-3)",
  "var(--series-4)",
  "var(--series-5)",
  "var(--series-6)",
];

export const MAX_SERIES = SERIES_SLOTS.length;

function isCategory(key: string): key is UsageCategory {
  return (USAGE_CATEGORIES as readonly string[]).includes(key);
}

/**
 * The colour of one key. `index` is its position in the ordered list, used only
 * when the key is not a category.
 */
export function colorForKey(key: string, index: number): string {
  if (isCategory(key)) return CATEGORY_COLORS[key];
  return SERIES_SLOTS[index % SERIES_SLOTS.length] ?? "var(--series-1)";
}

/**
 * What a key is called on screen. A category gets its spoken name; everything
 * else is a provider, a model, a product or a slug and says itself.
 */
export function labelForKey(key: string): string {
  return isCategory(key) ? CATEGORY_LABELS[key] : key;
}
